import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/ApiProvider.js';
import { ApiError } from '../api/client.js';
import { AddInline, QUERY_KEY, RenameInline, describeError } from './shared.js';
import { MAX_GOAL, effectiveJarTarget, jarTargetForGrade } from './jarTarget.js';
import {
  parseRewardsFile,
  type ParsedRewardsGrade,
  type RewardsParseError,
} from './rewardsImport.js';
import styles from './Rewards.module.css';

/**
 * Rewards management — lives in a classroom's Settings. Add/rename/delete the
 * grades you teach and the teacher (class) in each. The live Rewards tab reads
 * the same data (teacher-scoped) and is display-only.
 */
export function RewardsSettings() {
  const api = useApi();
  const queryClient = useQueryClient();

  const gradesQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.techTime.listGrades(),
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });

  const [selectedGradeId, setSelectedGradeId] = useState<string | null>(null);
  // Id (grade or class) currently showing an inline "Delete?" confirm.
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const grades = useMemo(() => gradesQuery.data ?? [], [gradesQuery.data]);
  const selectedGrade = grades.find((g) => g.id === selectedGradeId) ?? grades[0] ?? null;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const addGrade = useMutation({
    mutationFn: (name: string) => api.techTime.createGrade(name),
    onSuccess: (grade) => {
      void invalidate();
      setSelectedGradeId(grade.id);
    },
  });
  const renameGrade = useMutation({
    mutationFn: (v: { id: string; name: string }) => api.techTime.renameGrade(v.id, v.name),
    onSuccess: invalidate,
  });
  const setGoal = useMutation({
    mutationFn: (v: { id: string; goal: number | null }) => api.techTime.setGradeGoal(v.id, v.goal),
    onSuccess: invalidate,
  });
  const removeGrade = useMutation({
    mutationFn: (id: string) => api.techTime.removeGrade(id),
    onSuccess: () => {
      setSelectedGradeId(null);
      void invalidate();
    },
  });
  const addClass = useMutation({
    mutationFn: (v: { gradeId: string; teacherName: string }) =>
      api.techTime.createClass(v.gradeId, v.teacherName),
    onSuccess: invalidate,
  });
  const renameClass = useMutation({
    mutationFn: (v: { id: string; teacherName: string }) =>
      api.techTime.renameClass(v.id, v.teacherName),
    onSuccess: invalidate,
  });
  const removeClass = useMutation({
    mutationFn: (id: string) => api.techTime.removeClass(id),
    onSuccess: invalidate,
  });

  // ---- bulk import from a spreadsheet (2 cols: Grade, Teacher) ----
  const fileRef = useRef<globalThis.HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<ParsedRewardsGrade[]>([]);
  const [importErrors, setImportErrors] = useState<RewardsParseError[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseStatus, setParseStatus] = useState<'idle' | 'parsing' | 'error'>('idle');
  const [parseError, setParseError] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const [importSummary, setImportSummary] = useState<string | null>(null);

  const previewTeacherCount = preview.reduce((n, g) => n + g.teachers.length, 0);

  const importAll = useMutation({
    mutationFn: async (parsedGrades: ParsedRewardsGrade[]) => {
      // Snapshot what already exists so we reuse grades/teachers by name
      // (case-insensitive) instead of creating duplicates.
      const existing = new Map<string, { id: string; teachers: Set<string> }>();
      for (const g of grades) {
        existing.set(g.name.trim().toLowerCase(), {
          id: g.id,
          teachers: new Set(g.classes.map((c) => c.teacherName.trim().toLowerCase())),
        });
      }

      const total = parsedGrades.reduce((n, g) => n + g.teachers.length, 0);
      let done = 0;
      let gradesAdded = 0;
      let teachersAdded = 0;
      let teachersSkipped = 0;
      setImportProgress({ done: 0, total });

      for (const pg of parsedGrades) {
        const key = pg.name.trim().toLowerCase();
        let entry = existing.get(key);
        if (!entry) {
          const created = await api.techTime.createGrade(pg.name);
          entry = { id: created.id, teachers: new Set() };
          existing.set(key, entry);
          gradesAdded++;
        }
        for (const teacher of pg.teachers) {
          const tkey = teacher.trim().toLowerCase();
          if (entry.teachers.has(tkey)) {
            teachersSkipped++;
          } else {
            await api.techTime.createClass(entry.id, teacher);
            entry.teachers.add(tkey);
            teachersAdded++;
          }
          done++;
          setImportProgress({ done, total });
        }
      }
      return { gradesAdded, teachersAdded, teachersSkipped };
    },
    onSuccess: (r) => {
      void invalidate();
      const parts = [
        `${r.gradesAdded} grade${r.gradesAdded === 1 ? '' : 's'}`,
        `${r.teachersAdded} teacher${r.teachersAdded === 1 ? '' : 's'}`,
      ];
      const skipped = r.teachersSkipped > 0 ? ` · ${r.teachersSkipped} already there` : '';
      setImportSummary(`Loaded ${parts.join(' + ')}${skipped} ✓`);
      setPreview([]);
      setImportErrors([]);
      setFileName(null);
      setImportProgress(null);
    },
  });

  const onImportFile = async (e: ChangeEvent<globalThis.HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;

    setParseStatus('parsing');
    setParseError(null);
    setPreview([]);
    setImportErrors([]);
    setImportProgress(null);
    setImportSummary(null);
    setFileName(file.name);

    try {
      const result = await parseRewardsFile(file);
      setPreview(result.grades);
      setImportErrors(result.errors);
      setParseStatus(result.grades.length > 0 ? 'idle' : 'error');
      if (result.grades.length === 0 && result.errors.length === 0) {
        setParseError(
          'No rows found. Use 2 columns: grade in column 1, teacher in column 2 — one teacher per row.',
        );
      }
    } catch (err) {
      setParseStatus('error');
      setParseError(
        err instanceof Error ? `Couldn’t read the file: ${err.message}` : 'Couldn’t read the file.',
      );
      setFileName(null);
    }
  };

  const cancelImport = () => {
    setPreview([]);
    setImportErrors([]);
    setParseError(null);
    setImportProgress(null);
    setParseStatus('idle');
    setFileName(null);
  };

  const importing = importAll.isPending || importProgress !== null;

  const actionError =
    removeGrade.error ??
    removeClass.error ??
    addGrade.error ??
    addClass.error ??
    renameGrade.error ??
    renameClass.error ??
    setGoal.error ??
    importAll.error ??
    null;

  if (gradesQuery.error instanceof ApiError && gradesQuery.error.status === 403) {
    return (
      <div className={styles.settings}>
        <div className={styles.notice}>
          <p>Rewards is only available on the account it was set up for.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings} data-testid="rewards-settings">
      <p className={styles.hint}>
        Add the grades you teach and the teacher in each. The live <strong>Rewards</strong> tab
        shows only names — tap one to run the GAMES tracker.
      </p>

      {actionError ? (
        <div className={styles.errorBanner} role="alert" data-testid="rewards-error">
          That didn’t go through — {describeError(actionError)}
        </div>
      ) : null}

      <section className={styles.importPanel} data-testid="rewards-import-panel">
        <div className={styles.importRow}>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => fileRef.current?.click()}
            disabled={parseStatus === 'parsing' || importing}
            data-testid="rewards-import-button"
          >
            {parseStatus === 'parsing' ? 'Reading…' : 'Upload from Excel…'}
          </button>
          <span className={styles.hint}>
            .xlsx, .xls, or .csv — 2 columns: <strong>Grade</strong> in column 1,{' '}
            <strong>Teacher</strong> in column 2 (one teacher per row).
          </span>
        </div>

        <input
          ref={fileRef}
          type="file"
          className={styles.fileInput}
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={onImportFile}
          data-testid="rewards-import-file"
        />

        {parseError ? (
          <p className={styles.importError} data-testid="rewards-import-parse-error">
            {parseError}
          </p>
        ) : null}

        {importSummary ? (
          <p className={styles.importSuccess} data-testid="rewards-import-loaded">
            {importSummary}
          </p>
        ) : null}

        {preview.length > 0 || importErrors.length > 0 ? (
          <div className={styles.importPreview} data-testid="rewards-import-preview">
            <div className={styles.importPreviewHead}>
              <span>
                {preview.length > 0
                  ? `Found ${preview.length} grade${preview.length === 1 ? '' : 's'} · ${previewTeacherCount} teacher${previewTeacherCount === 1 ? '' : 's'}`
                  : 'No valid rows'}
                {fileName ? ` in ${fileName}` : ''}
                {importErrors.length > 0 ? ` · ${importErrors.length} skipped` : ''}
              </span>
              <span className={styles.confirmRow}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={cancelImport}
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => importAll.mutate(preview)}
                  disabled={preview.length === 0 || importing}
                  data-testid="rewards-import-confirm"
                >
                  {importing
                    ? `Loading ${importProgress?.done ?? 0} of ${importProgress?.total ?? previewTeacherCount}…`
                    : `Load ${preview.length} grade${preview.length === 1 ? '' : 's'}`}
                </button>
              </span>
            </div>

            {preview.length > 0 ? (
              <ul className={styles.importGradeList}>
                {preview.map((g) => (
                  <li key={g.name}>
                    <strong>{g.name}</strong> — {g.teachers.join(', ')}
                  </li>
                ))}
              </ul>
            ) : null}

            {importErrors.length > 0 ? (
              <div className={styles.importSkipped}>
                <span className={styles.manageLabel}>Skipped rows</span>
                {importErrors.slice(0, 10).map((err, i) => (
                  <span key={i} className={styles.importSkippedItem}>
                    #{err.row} {err.reason} — “{err.raw}”
                  </span>
                ))}
                {importErrors.length > 10 ? (
                  <span className={styles.importSkippedItem}>
                    …and {importErrors.length - 10} more
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className={styles.managePanel} data-testid="rewards-manage-panel">
        <div className={styles.manageGroup}>
          <span className={styles.manageLabel}>Add a grade</span>
          <AddInline
            placeholder="Grade name"
            label="Add grade"
            testId="rewards-add-grade"
            pending={addGrade.isPending}
            onAdd={(name) => addGrade.mutate(name)}
          />
        </div>
        <div className={styles.manageGroup}>
          <span className={styles.manageLabel}>
            {selectedGrade ? `Add a teacher to ${selectedGrade.name}` : 'Add a teacher'}
          </span>
          {selectedGrade ? (
            <AddInline
              placeholder="Teacher name"
              label="Add teacher"
              testId="rewards-add-class"
              pending={addClass.isPending}
              onAdd={(teacherName) => addClass.mutate({ gradeId: selectedGrade.id, teacherName })}
            />
          ) : (
            <p className={styles.hint}>Add a grade first, then select it below.</p>
          )}
        </div>
      </section>

      {grades.length > 0 ? (
        <nav className={styles.grades} aria-label="Grade levels">
          {grades.map((g) => (
            <button
              key={g.id}
              type="button"
              className={g.id === selectedGrade?.id ? styles.gradeTabActive : styles.gradeTab}
              onClick={() => {
                setSelectedGradeId(g.id);
                setConfirmId(null);
              }}
              data-testid="rewards-grade-tab"
            >
              {g.name}
            </button>
          ))}
        </nav>
      ) : null}

      {selectedGrade ? (
        <section className={styles.expand}>
          <div className={styles.gradeManageRow}>
            <span className={styles.manageLabel}>Grade</span>
            <RenameInline
              key={selectedGrade.id}
              current={selectedGrade.name}
              onRename={(name) => renameGrade.mutate({ id: selectedGrade.id, name })}
            />
            {confirmId === selectedGrade.id ? (
              <span className={styles.confirmRow}>
                <button
                  type="button"
                  className={styles.confirmDeleteBtn}
                  onClick={() => {
                    removeGrade.mutate(selectedGrade.id);
                    setConfirmId(null);
                  }}
                >
                  Delete grade + all teachers
                </button>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setConfirmId(null)}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={() => setConfirmId(selectedGrade.id)}
              >
                Delete grade
              </button>
            )}
          </div>

          <GoalEditor
            key={`goal-${selectedGrade.id}-${selectedGrade.goal ?? 'def'}`}
            gradeName={selectedGrade.name}
            goal={selectedGrade.goal}
            pending={setGoal.isPending}
            onSet={(goal) => setGoal.mutate({ id: selectedGrade.id, goal })}
          />

          <ul className={styles.manageTeacherList} aria-label={`${selectedGrade.name} teachers`}>
            {selectedGrade.classes.map((c) => (
              <li key={c.id} className={styles.classItem}>
                <RenameInline
                  key={c.id + c.teacherName}
                  current={c.teacherName}
                  onRename={(teacherName) => renameClass.mutate({ id: c.id, teacherName })}
                />
                {confirmId === c.id ? (
                  <span className={styles.confirmRow}>
                    <button
                      type="button"
                      className={styles.confirmDeleteBtn}
                      onClick={() => {
                        removeClass.mutate(c.id);
                        setConfirmId(null);
                      }}
                      data-testid="rewards-confirm-delete"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className={styles.cancelBtn}
                      onClick={() => setConfirmId(null)}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => setConfirmId(c.id)}
                    aria-label={`Delete ${c.teacherName}`}
                    data-testid="rewards-delete-class"
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>

          {selectedGrade.classes.length === 0 ? (
            <p className={styles.hint}>No teachers yet — add one above.</p>
          ) : null}
        </section>
      ) : !gradesQuery.isLoading && grades.length === 0 ? (
        <p className={styles.hint}>No grades yet — add your first one above.</p>
      ) : null}
    </div>
  );
}

/**
 * Manual jar-goal editor for a grade. Shows the goal in effect (the override,
 * or the grade-name default) and lets the teacher set a new number — e.g. bump
 * a grade after they earn their first free day. "Use default" clears the
 * override back to the name-based value.
 */
function GoalEditor(props: {
  gradeName: string;
  goal: number | null;
  pending: boolean;
  onSet: (goal: number | null) => void;
}) {
  const defaultGoal = jarTargetForGrade(props.gradeName);
  const effective = effectiveJarTarget(props.gradeName, props.goal);
  const [value, setValue] = useState(String(effective));

  const save = () => {
    const n = Math.round(Number(value));
    if (!Number.isFinite(n) || n < 1 || n > MAX_GOAL) return;
    if (n === effective) return;
    props.onSet(n);
  };

  return (
    <div className={styles.goalRow} data-testid="rewards-goal">
      <span className={styles.manageLabel}>Jar goal</span>
      <input
        className={styles.goalInput}
        type="number"
        min={1}
        max={MAX_GOAL}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
        }}
        aria-label={`Jar goal for ${props.gradeName}`}
        data-testid="rewards-goal-input"
      />
      <span className={styles.goalUnit}>points</span>
      <button
        type="button"
        className={styles.addBtn}
        onClick={save}
        disabled={props.pending}
        data-testid="rewards-goal-save"
      >
        Save
      </button>
      {props.goal !== null ? (
        <button
          type="button"
          className={styles.goalDefaultBtn}
          onClick={() => props.onSet(null)}
          disabled={props.pending}
          data-testid="rewards-goal-default"
        >
          Use default ({defaultGoal})
        </button>
      ) : (
        <span className={styles.hint}>Default for this grade</span>
      )}
    </div>
  );
}
